import React, { useId } from 'react';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { Contact } from '../../../../types';
import { EditableContactField } from '../../../../components/shared/InlineEditable';

interface ContactPanelInfoProps {
    isOpen: boolean;
    onToggle: () => void;
    conversationId: string;
    currentContact: Contact | null | undefined;
    onUpdateContactField: (field: 'company' | 'email' | 'phone' | 'whatsapp', value: string) => void;
    /** Canal da conversa — Instagram mostra @username no lugar do telefone */
    chatPlatform?: 'whatsapp' | 'instagram';
    /** Já formatado (@username); só Instagram */
    instagramHandle?: string | null;
}

export const ContactPanelInfo: React.FC<ContactPanelInfoProps> = ({
    isOpen,
    onToggle,
    conversationId,
    currentContact,
    onUpdateContactField,
    chatPlatform,
    instagramHandle,
}) => {
    const displayCompany = currentContact?.company || '';
    const displayEmail = currentContact?.email || '';

    const isInstagram = chatPlatform === 'instagram';
    const phoneNumber = conversationId.replace(/\D/g, '');
    const formattedPhone = phoneNumber ? `+${phoneNumber}` : '';
    const identityValue = isInstagram
        ? (instagramHandle || 'Não informado')
        : (formattedPhone || 'Não informado');

    const panelId = useId();
    const contactInfoCount = [displayCompany, isInstagram ? instagramHandle : formattedPhone, displayEmail]
        .filter(info => info && info.trim() !== '').length;

    return (
        <section>
            <button
                type="button"
                onClick={onToggle}
                className="flex min-h-11 w-full items-center justify-between px-3 py-2"
                aria-expanded={isOpen}
                aria-controls={panelId}
            >
                <div className="flex items-center gap-2 chat-section-heading">
                    <span>Contato</span>
                    {contactInfoCount > 0 && (
                        <span className="chat-count rounded-full border border-border bg-surface-subtle px-1.5 py-0 leading-5 text-muted">
                            {contactInfoCount}
                        </span>
                    )}
                </div>
                <AppIcon
                    name="chevron-down"
                    className={`chat-accordion-chevron ${isOpen ? '' : 'chat-accordion-chevron--closed'}`}
                    aria-hidden="true"
                />
            </button>

            {isOpen && (
                <div id={panelId} className="bg-card px-3 pb-3">
                    <div className="space-y-1.5 rounded-lg border border-border bg-surface p-2.5">
                        <EditableContactField
                            icon="building"
                            value={displayCompany}
                            placeholder="Adicionar empresa"
                            onSave={(value) => onUpdateContactField('company', value)}
                        />

                        <div className="flex min-h-11 items-center gap-2 pointer-fine:min-h-9">
                            <AppIcon
                                name={isInstagram ? 'user' : 'phone'}
                                className="size-4 shrink-0 text-muted"
                                aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1 truncate text-body-sm text-muted tabular-nums">
                                {identityValue}
                            </span>
                            <span className="inline-flex shrink-0 items-center gap-1 text-muted" title="Campo bloqueado">
                                <AppIcon name="lock" className="size-3.5 opacity-50" aria-hidden="true" />
                                <span className="sr-only">
                                    {isInstagram ? 'Usuário do Instagram' : 'Telefone'} bloqueado para edição
                                </span>
                            </span>
                        </div>

                        <EditableContactField
                            icon="mail"
                            value={displayEmail}
                            placeholder="Adicionar email"
                            type="email"
                            onSave={(value) => onUpdateContactField('email', value)}
                        />
                    </div>
                </div>
            )}
        </section>
    );
};
